import type { AppState } from "./appState";
import { createLogger } from "@/logging";

export type AppStateInitializeCallback = (state: AppState) => void | Promise<void>;

const logger = createLogger("appStateHandler");

class AppStateHandler {
  private initializedState: AppState | null = null;
  private readonly initializeCallbacks = new Set<AppStateInitializeCallback>();

  onInitialize(callback: AppStateInitializeCallback): () => void {
    if (this.initializedState !== null) {
      queueMicrotask(() => {
        void this.runInitializeCallback(callback, this.initializedState as AppState);
      });
      return () => undefined;
    }

    this.initializeCallbacks.add(callback);
    return () => {
      this.initializeCallbacks.delete(callback);
    };
  }

  async notifyInitialized(state: AppState): Promise<void> {
    if (this.initializedState !== null) {
      return;
    }

    this.initializedState = state;
    const callbacks = [...this.initializeCallbacks];
    await Promise.all(callbacks.map((callback) => this.runInitializeCallback(callback, state)));
  }

  private async runInitializeCallback(
    callback: AppStateInitializeCallback,
    state: AppState,
  ): Promise<void> {
    try {
      await callback(state);
    } catch (error) {
      logger.error("onInitialize callback failed.", error);
    }
  }
}

export const appStateHandler = new AppStateHandler();
